import { useCallback, useRef, useState } from 'react'

export type BlockingActionState = {
  open: boolean
  title: string
  message: string
  detail?: string
}

type BlockingActionOptions = {
  title?: string
  message?: string
  detail?: string
}

const DEFAULT_BLOCKING_STATE: BlockingActionState = {
  open: false,
  title: '处理中',
  message: '请求正在处理，请稍候…',
}

/**
 * 单飞异步操作：同一操作未结束前再次触发会被忽略，并可直接驱动 BlockingModal。
 */
export function useBlockingAction(defaults?: BlockingActionOptions) {
  const runningRef = useRef(false)
  const [state, setState] = useState<BlockingActionState>(DEFAULT_BLOCKING_STATE)

  const run = useCallback(
    async <T,>(action: () => Promise<T>, options?: BlockingActionOptions): Promise<T | undefined> => {
      if (runningRef.current) return undefined
      runningRef.current = true
      const next = { ...defaults, ...options }
      setState({
        open: true,
        title: next.title ?? DEFAULT_BLOCKING_STATE.title,
        message: next.message ?? DEFAULT_BLOCKING_STATE.message,
        detail: next.detail,
      })
      try {
        return await action()
      } finally {
        runningRef.current = false
        setState((prev) => ({ ...prev, open: false }))
      }
    },
    [defaults],
  )

  return {
    blockingState: state,
    runBlocking: run,
    blocking: state.open,
  }
}
